import { jsx } from "react/jsx-runtime";
import { styled } from "@mui/material/styles";
import Skeleton from "@mui/material/Skeleton";
import { barClasses, barElementClasses, BarChart as BarChart$1 } from "@mui/x-charts/BarChart";
import { useMemo } from "react";
import { makeValueFormatter, getYAxisWidth } from "../utils/data.js";
import { sprintf } from "sprintf-js";
const StyledBarChart = styled(BarChart$1, {
  shouldForwardProp: prop => !["showLabels"].includes(prop)
})(({
  theme,
  showLabels
}) => ({
  display: "flex",
  flexDirection: "column",
  padding: theme.sizes.spacing["01"],
  margin: theme.sizes.spacing["01"],
  position: "relative",
  ["& .MuiBarElement-root"]: {
    strokeWidth: 1
  },
  ["& .MuiChartsAxis-root"]: {
    ...(showLabels ? {} : {
      display: "none"
    }),
    "& .MuiChartsAxis-line, & .MuiChartsAxis-tick, .MuiChartsAxis-label": {
      display: "none"
    }
  }
}));
const formatLabel = (value, format) => {
  if (!format) return `${value}`;
  try {
    return sprintf(format, value);
  } catch {
    return `${value}`;
  }
};
const BarChart = ({
  seriesData,
  isLoading = false,
  configuration,
  showLabels = false,
  series,
  height,
  colorScheme
}) => {
  const labelsKey = configuration.x.data.key.split(".").pop() || "";
  const valuesKey = series[0]?.data.key.split(".").pop() || "";
  const dataset = useMemo(() => {
    if (!seriesData || isLoading) {
      return null;
    }
    const {
      data,
      columns
    } = seriesData;
    const labelsIdx = columns.findIndex(col => col.key === labelsKey);
    const labelFormat = columns.find(col_0 => col_0.key === labelsKey)?.label_format;
    const valuesIdx = columns.findIndex(col_1 => col_1.key === valuesKey);
    const ds = data.map(row => ({
      label: formatLabel(row[labelsIdx], labelFormat),
      value: row[valuesIdx]
    }));
    return ds;
  }, [seriesData, isLoading, labelsKey, valuesKey]);
  const barGradientId = `${colorScheme}-bar-gradient`;
  const valueCol = seriesData?.columns.find(c => c.key === valuesKey);
  const valueFormatter = makeValueFormatter(valueCol);
  const axisFormatter = makeValueFormatter(valueCol, 0);
  const axisWidth = useMemo(() => {
    if (!showLabels || !dataset?.length) return 0;
    const maxVal = dataset?.reduce((m, item) => Math.max(m, Math.abs(item.value)), 0);
    const repr = axisFormatter ? axisFormatter(maxVal) : Math.ceil(maxVal).toString();
    return getYAxisWidth(repr.length);
  }, [dataset, showLabels, axisFormatter]);
  if (!dataset || isLoading) {
    return /* @__PURE__ */jsx(Skeleton, {
      variant: "rectangular",
      width: "100%",
      height: 260
    });
  }
  return /* @__PURE__ */jsx("div", {
    style: {
      width: "100%"
    },
    children: /* @__PURE__ */jsx(StyledBarChart, {
      skipAnimation: true,
      dataset,
      height: height - 4,
      borderRadius: 6,
      showLabels,
      grid: {
        horizontal: true,
        vertical: false
      },
      series: [{
        dataKey: "value",
        type: "bar",
        color: "transparent",
        id: "1",
        ...(valueFormatter ? {
          valueFormatter
        } : {})
      }],
      margin: {
        left: 0,
        right: 0,
        bottom: 0
      },
      yAxis: [showLabels ? {
        width: axisWidth,
        ...(axisFormatter ? {
          valueFormatter: axisFormatter
        } : {})
      } : {
        width: axisWidth,
        position: "none"
      }],
      xAxis: [{
        id: "x",
        scaleType: "band",
        data: dataset.map(d => d.label),
        categoryGapRatio: 0.5,
        ...(showLabels ? {} : {
          position: "none",
          height: 0
        })
      }],
      sx: {
        [`& .${barClasses.series}[data-series="1"] .${barElementClasses.root}`]: {
          fill: `url(#${barGradientId})`
        }
      }
    })
  });
};
export { BarChart as default };